import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import Navbar from "@/components/Navbar";
import AnnouncementBar from "@/components/AnnouncementBar";
import Footer from "@/components/Footer";
import { INSIGHTS } from "@/pages/Insights";
import ChatPanel from "@/components/chat/ChatPanel";

export default function InsightArticle() {
  const { slug } = useParams();
  const item = INSIGHTS.find((i) => i.slug === slug);
  if (!item) return <Navigate to="/insights" replace />;

  const related = INSIGHTS.filter((i) => i.slug !== slug).slice(0, 3);

  const articleContext = {
    title: item.title,
    tag: item.tag,
    excerpt: item.excerpt,
    body: item.body.map((b) => `${b.h}\n${b.p}`).join("\n\n"),
  };

  return (
    <main data-testid="insight-article-page" className="relative min-h-screen">
      <AnnouncementBar />
      <Navbar />

      <article className="pt-32 md:pt-40 pb-16">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-teal mb-8"
          >
            <ArrowLeft size={14} /> All insights
          </Link>
          <div className="flex items-center gap-3 mb-6">
            <span className="rounded-full border border-teal/30 bg-teal/[0.06] text-teal text-[10px] font-mono uppercase tracking-[0.18em] px-3 py-1">
              {item.tag}
            </span>
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              {item.date}
            </span>
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-1">
              <Clock size={11} /> {item.read}
            </span>
          </div>
          <h1 className="font-display font-semibold text-4xl md:text-5xl lg:text-[68px] leading-[1] tracking-[-0.02em]">
            {item.title}
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">
            {item.excerpt}
          </p>
        </div>

        <div className="mt-12 md:mt-16 relative aspect-[16/8] mx-auto max-w-[1200px] px-6 lg:px-10">
          <img
            src={item.hero}
            alt=""
            className="h-full w-full object-cover rounded-2xl border border-white/[0.06]"
          />
        </div>

        <div className="mt-16 mx-auto max-w-[760px] px-6 lg:px-10 space-y-12">
          {item.body.map((b) => (
            <section key={b.h}>
              <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight mb-4 leading-tight">
                {b.h}
              </h2>
              <p className="text-[15px] md:text-base text-foreground/85 leading-[1.75]">
                {b.p}
              </p>
            </section>
          ))}

          <div className="rounded-2xl border border-teal/25 bg-teal/[0.03] p-6 md:p-8 mt-16">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-teal mb-2">
              Want the full playbook?
            </div>
            <div className="font-display text-2xl font-semibold leading-tight mb-3">
              Book a 30-min working session with a RevGrow strategist.
            </div>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-1.5 rounded-full bg-teal text-[#050A0F] px-5 py-2.5 text-[13px] font-medium hover:opacity-90"
            >
              Book a strategy call <ArrowUpRight size={15} />
            </Link>
          </div>

          {/* Article AI Assistant */}
          <div className="mt-10">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-3">
              Ask about this piece
            </div>
            <ChatPanel
              mode="article"
              sessionKey={`article-${slug}`}
              variant="inline"
              article={articleContext}
              title="Article assistant"
              subtitle="Grounded in this article · Gemini 2.5 Flash"
              placeholder="Ask about this article..."
              initialMessage={`Hey — I've read "${item.title}" and I can help you dig into it. What would you like to explore?`}
              suggestions={[
                "Summarise the key takeaway",
                "How does this apply to a UK CIO?",
                "What should my team do first?",
              ]}
            />
          </div>
        </div>
      </article>

      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-6">
            Related insights
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/insights/${r.slug}`}
                className="rounded-2xl border border-white/[0.06] bg-[#0A121A] overflow-hidden card-hover"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img src={r.hero} alt="" className="absolute inset-0 h-full w-full object-cover opacity-70" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A121A]/80 to-transparent" />
                </div>
                <div className="p-6">
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-teal mb-2">{r.tag}</div>
                  <div className="font-display text-base font-semibold leading-tight">{r.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
